import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalStorage } from './useLocalStorage';
import { useProgress } from '@/contexts/ProgressContext';

export type PomoPhase = 'work' | 'short-break' | 'long-break' | 'idle';

export interface PomoSettings {
  workMin: number;
  shortBreakMin: number;
  longBreakMin: number;
  longBreakAfter: number;
  soundOn: boolean;
  notifOn: boolean;
  autoStart: boolean;
  /** Sound preset: chime · soft · bell. */
  soundPreset: 'chime' | 'soft' | 'bell';
}

export const DEFAULT_POMO_SETTINGS: PomoSettings = {
  workMin: 25,
  shortBreakMin: 5,
  longBreakMin: 15,
  longBreakAfter: 4,
  soundOn: true,
  notifOn: false,
  autoStart: false,
  soundPreset: 'soft',
};

interface PomoLog {
  /** ISO date 'YYYY-MM-DD' for daily counter reset. */
  date: string;
  /** Number of completed work sessions today. */
  todayCount: number;
  /** Cycles completed in the current work session block (resets after long break). */
  cycleCount: number;
  /** Last reflection draft to support cross-mount restore. */
  pendingReflection: boolean;
}

const today = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const SETTINGS_KEY = 'qastudio.v1.ui.pomo';
const LOG_KEY = 'qastudio.v1.pomo.log';

interface UsePomodoroResult {
  phase: PomoPhase;
  remainingSec: number;
  totalSec: number;
  running: boolean;
  todayCount: number;
  cycleCount: number;
  settings: PomoSettings;
  pendingReflection: boolean;
  start: () => void;
  pause: () => void;
  reset: () => void;
  skip: () => void;
  acknowledgeReflection: () => void;
  setSettings: (next: PomoSettings | ((prev: PomoSettings) => PomoSettings)) => void;
  /** mm:ss formatted remaining. */
  display: string;
  /** 0..1 progress inside current phase. */
  progress: number;
}

const fmt = (sec: number): string => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const ensureNotificationPermission = async (): Promise<void> => {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission === 'granted' || Notification.permission === 'denied') return;
  try {
    await Notification.requestPermission();
  } catch {
    /* ignore */
  }
};

const notifyPhaseEnd = (next: PomoPhase, settings: PomoSettings): void => {
  if (!settings.notifOn || typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;
  const title =
    next === 'work'
      ? 'Перерыв окончен · фокус на работу'
      : next === 'short-break'
        ? 'Сессия закончена · короткий перерыв'
        : next === 'long-break'
          ? 'Серия завершена · длинный перерыв'
          : 'Pomodoro';
  try {
    new Notification(title, { body: 'QA Studio · Pomodoro', silent: !settings.soundOn });
  } catch {
    /* ignore */
  }
};

const playCue = (preset: PomoSettings['soundPreset']): void => {
  if (typeof window === 'undefined' || !('AudioContext' in window || 'webkitAudioContext' in window)) {
    return;
  }
  try {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new Ctor();
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = preset === 'bell' ? 'triangle' : preset === 'chime' ? 'sine' : 'sine';
    const baseFreq = preset === 'bell' ? 880 : preset === 'chime' ? 660 : 520;
    osc.frequency.setValueAtTime(baseFreq, now);
    osc.frequency.exponentialRampToValueAtTime(baseFreq * 0.6, now + 0.6);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.18, now + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.7);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.75);
    osc.onended = () => ctx.close();
  } catch {
    /* AudioContext blocked / not allowed yet */
  }
};

/** Full Pomodoro state machine · 25/5/15 default · long break after N cycles ·
 *  audio cue · browser notification · daily counter · reflection prompt. */
export function usePomodoro(): UsePomodoroResult {
  const [settings, setSettings] = useLocalStorage<PomoSettings>(SETTINGS_KEY, DEFAULT_POMO_SETTINGS);
  const [log, setLog] = useLocalStorage<PomoLog>(LOG_KEY, {
    date: today(),
    todayCount: 0,
    cycleCount: 0,
    pendingReflection: false,
  });

  // PR.2 integration · 2026-05-17 slot_pomodoro_rework. Wire usePomodoro к
  // progress.achievements.pomodoroSessions[] (ADR-009). Captures hour-of-day на
  // work-session start · appends событие через recordPomodoroSession при
  // work-phase complete (rollover work→break). Fixes Dashboard «Время в фокусе»
  // KPI silent-zero bug + unblocks badge triggers (night-owl · early-bird ·
  // pomodoro-time-window) которые читают progress.achievements.pomodoroSessions.
  const { recordPomodoroSession } = useProgress();
  const startHourRef = useRef<number | null>(null);

  const [phase, setPhase] = useState<PomoPhase>('idle');
  const [remainingSec, setRemainingSec] = useState<number>(settings.workMin * 60);
  const [running, setRunning] = useState<boolean>(false);
  const tickRef = useRef<number | null>(null);

  // PR.2 integration · capture hour-of-day на work-session start. Effect
  // triggers когда state становится (phase=work, running=true) · regardless of
  // path (idle start · resume after pause · auto-transition from break with
  // autoStart). Cleared at session complete (rollover work→break) OR reset() ·
  // next entry captures fresh hour. Single source of truth = no scattered
  // capture logic в start/auto-transition callbacks.
  useEffect(() => {
    if (phase === 'work' && running && startHourRef.current === null) {
      startHourRef.current = new Date().getHours();
    }
  }, [phase, running]);

  // Reset daily counter when crossing midnight.
  useEffect(() => {
    const t = today();
    if (log.date !== t) {
      setLog({ date: t, todayCount: 0, cycleCount: 0, pendingReflection: false });
    }
  }, [log.date, setLog]);

  const totalSec = useMemo(() => {
    if (phase === 'short-break') return settings.shortBreakMin * 60;
    if (phase === 'long-break') return settings.longBreakMin * 60;
    return settings.workMin * 60;
  }, [phase, settings.shortBreakMin, settings.longBreakMin, settings.workMin]);

  // Tick once per second when running.
  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => {
      setRemainingSec((prev) => Math.max(0, prev - 1));
    }, 1000);
    tickRef.current = id;
    return () => {
      if (tickRef.current !== null) window.clearInterval(tickRef.current);
    };
  }, [running]);

  // Phase rollover when timer hits 0.
  useEffect(() => {
    if (remainingSec > 0 || !running) return;
    setRunning(false);
    if (phase === 'work') {
      const nextCycleCount = log.cycleCount + 1;
      const next: PomoPhase =
        nextCycleCount >= settings.longBreakAfter ? 'long-break' : 'short-break';
      const nextLen = next === 'short-break' ? settings.shortBreakMin : settings.longBreakMin;
      setLog((prev) => ({
        ...prev,
        date: today(),
        todayCount: prev.todayCount + 1,
        cycleCount: next === 'long-break' ? 0 : nextCycleCount,
        pendingReflection: true,
      }));
      // PR.2 integration · append завершённую work-session в
      // progress.achievements.pomodoroSessions[] (ADR-009). startHour captured
      // earlier (capture-on-enter-work effect) · survives pause/resume cycles.
      // Clear ref после record · next work session capture fresh hour.
      if (startHourRef.current !== null) {
        recordPomodoroSession(startHourRef.current);
        startHourRef.current = null;
      }
      setPhase(next);
      setRemainingSec(nextLen * 60);
      if (settings.soundOn) playCue(settings.soundPreset);
      notifyPhaseEnd(next, settings);
      if (settings.autoStart) setRunning(true);
    } else if (phase === 'short-break' || phase === 'long-break') {
      setPhase('work');
      setRemainingSec(settings.workMin * 60);
      if (settings.soundOn) playCue(settings.soundPreset);
      notifyPhaseEnd('work', settings);
      if (settings.autoStart) setRunning(true);
    }
  }, [remainingSec, running, phase, log.cycleCount, settings, setLog, recordPomodoroSession]);

  const start = useCallback(() => {
    if (settings.notifOn) void ensureNotificationPermission();
    setPhase((prev) => (prev === 'idle' ? 'work' : prev));
    setRunning(true);
  }, [settings.notifOn]);
  const pause = useCallback(() => setRunning(false), []);
  const reset = useCallback(() => {
    setRunning(false);
    setPhase('idle');
    setRemainingSec(settings.workMin * 60);
    // PR.2 integration · cancel session · clear startHour · incomplete work
    // не записывается в pomodoroSessions (only completed work counts).
    startHourRef.current = null;
  }, [settings.workMin]);

  const skip = useCallback(() => {
    setRemainingSec(0);
  }, []);

  const acknowledgeReflection = useCallback(() => {
    setLog((prev) => ({ ...prev, pendingReflection: false }));
  }, [setLog]);

  // Reflect settings.workMin changes when idle.
  useEffect(() => {
    if (phase === 'idle' && !running) {
      setRemainingSec(settings.workMin * 60);
    }
  }, [phase, running, settings.workMin]);

  return {
    phase,
    remainingSec,
    totalSec,
    running,
    todayCount: log.date === today() ? log.todayCount : 0,
    cycleCount: log.cycleCount,
    settings,
    pendingReflection: log.pendingReflection,
    start,
    pause,
    reset,
    skip,
    acknowledgeReflection,
    setSettings,
    display: fmt(remainingSec),
    progress: totalSec === 0 ? 0 : 1 - remainingSec / totalSec,
  };
}
